<template>
    <div class="">
        <div class="flex mt-[42px] mb-[27px] justify-between h-[42px]">
            <h1
                class="text-[21px] font-medium leading-normal tracking-small text-gray-800"
            >
                {{ $t("vue.billing") }}
            </h1>
        </div>

        <BillingTabs class="mt-[24px]" tab-key="subscriptions">
            <WarningBlock
                v-if="billingAdminTeamRoles.includes(props.teamRole)"
                :class="'mt-[24px]'"
                :title="t('vue.limited_access')"
                :description="
                    t('vue.limited_access_description', {
                        date: freePlanResetDateLabel,
                    })
                "
            />

            <div class="mb-[98px]">
                <!-- 1) Your Current Plan -->
                <section class="mt-[42px] grid-cols-layout pb-5 lg:grid">
                    <div class="mb-6 lg:mb-0 lg:mt-4">
                        <h3
                            class="mb-[5px] text-[15.75px] font-medium leading-[142%] tracking-[-0.01em] text-[#161E2E]"
                        >
                            {{ $t("vue.your_current_plan") }}
                        </h3>
                        <p
                            class="max-w-[333px] text-[12.25px] font-normal leading-[142%] text-[#4B5563]"
                        >
                            {{ $t("vue.your_current_dmarc_subscription") }}
                        </p>
                    </div>

                    <div
                        v-if="isLoading"
                        class="mx-auto w-full rounded-2xl border border-[#E5E7EB] bg-white shadow-sm lg:mx-0"
                    >
                        <div class="p-6">
                            <div
                                class="h-7 w-56 animate-pulse rounded rounded-[12.8px] border border-[#E5E7EB] bg-[#F3F4F6]"
                            ></div>
                            <div
                                class="mt-2 h-4 w-72 max-w-full animate-pulse rounded-[12.8px] border border-[#E5E7EB] bg-[#F3F4F6]"
                            ></div>

                            <div
                                class="mt-5 rounded-[12.8px] border border-[#E5E7EB] bg-[#F3F4F6] p-6"
                            >
                                <div
                                    v-for="idx in 4"
                                    :key="`current-plan-loading-${idx}`"
                                    class="grid grid-cols-[160px_1fr] gap-4"
                                >
                                    <div
                                        class="h-4 w-full animate-pulse rounded-[12.8px] border border-[#E5E7EB] bg-[#F3F4F6]"
                                    ></div>
                                    <div
                                        class="h-4 w-48 max-w-full animate-pulse rounded-[12.8px] border border-[#E5E7EB] bg-[#F3F4F6]"
                                    ></div>
                                </div>
                            </div>

                            <div
                                class="mt-6 h-[46px] w-full animate-pulse rounded-xl rounded-[12.8px] border border-[#E5E7EB] bg-[#F3F4F6]"
                            ></div>

                            <div class="mt-6 flex flex-wrap justify-end gap-3">
                                <div
                                    class="h-10 w-52 animate-pulse rounded-xl rounded-[12.8px] border border-[#E5E7EB] bg-[#F3F4F6]"
                                ></div>
                                <div
                                    class="h-10 w-40 animate-pulse rounded-xl rounded-[12.8px] border border-[#E5E7EB] bg-[#F3F4F6]"
                                ></div>
                            </div>
                        </div>
                    </div>

                    <div
                        v-else
                        class="mx-auto w-full rounded-2xl border border-[#E5E7EB] bg-white shadow-sm lg:mx-0"
                    >
                        <div class="p-6">
                            <div
                                class="flex flex-wrap items-start justify-between gap-4"
                            >
                                <div>
                                    <h2
                                        class="text-xl font-semibold text-slate-900"
                                    >
                                        {{ currentPlanTitle }}
                                    </h2>
                                    <p class="mt-1 text-sm text-slate-500">
                                        {{ currentPlanSubtitle }}
                                    </p>
                                </div>
                            </div>

                            <dl class="mt-6 grid gap-y-3">
                                <div
                                    v-for="row in currentPlanRows"
                                    :key="row.label"
                                    class="grid grid-cols-[160px_1fr] gap-4"
                                >
                                    <dt
                                        class="text-sm font-semibold text-slate-900"
                                    >
                                        {{ row.label }}
                                    </dt>
                                    <dd class="text-sm text-slate-500">
                                        <span
                                            v-if="row.onClick"
                                            class="cursor-pointer font-semibold text-orange-500"
                                            @click="row.onClick()"
                                        >
                                            {{ row.value }}
                                        </span>
                                        <span v-else>
                                            {{ row.value }}
                                        </span>
                                    </dd>
                                </div>
                            </dl>

                            <div
                                v-if="currentPlanNotice"
                                class="mt-6 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3"
                            >
                                <div class="flex gap-3">
                                    <span class="mt-0.5 text-amber-600"
                                        >⚠️</span
                                    >
                                    <p class="text-sm text-amber-800">
                                        {{ currentPlanNotice }}
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div
                            v-if="
                                isBillingManagementRole && showManagePortalBtn
                            "
                            class="flex justify-end border-t border-[#EDEFF1] px-[21px] py-[21px]"
                        >
                            <AtomicButton
                                v-if="showManagePortalBtn"
                                class="mt-0"
                                color="light-blue"
                                @click="visitCustomerPortal()"
                            >
                                {{ $t("vue.manage_subscription_invoices") }}
                            </AtomicButton>
                        </div>
                    </div>
                </section>

                <!-- 2) All Plans / Available subscriptions -->
                <section
                    v-if="!hasGodMode"
                    class="mt-5 grid-cols-layout pb-5 lg:grid"
                >
                    <div class="mb-6 lg:mb-0 lg:mt-4">
                        <h3
                            class="mb-[5px] text-[15.75px] font-medium leading-[142%] tracking-[-0.01em] text-[#161E2E]"
                        >
                            {{ plansSectionTitle }}
                        </h3>
                        <p
                            class="max-w-[333px] text-[12.25px] font-normal leading-[142%] text-[#4B5563]"
                        >
                            {{ $t("vue.check_pricing_page_info") }}
                        </p>
                    </div>

                    <div class="w-full">
                        <div v-if="isLoading" class="grid gap-6 lg:grid-cols-2">
                            <div
                                class="flex h-full flex-col rounded-2xl border border-[#E5E7EB] bg-white shadow-sm"
                            >
                                <div class="flex-1 p-6">
                                    <div
                                        class="flex items-start justify-between gap-4"
                                    >
                                        <div class="w-full">
                                            <div
                                                class="h-6 w-24 animate-pulse rounded rounded-[12.8px] border border-[#E5E7EB] bg-[#F3F4F6]"
                                            ></div>
                                            <div
                                                class="mt-2 h-4 w-3/4 animate-pulse rounded rounded-[12.8px] border border-[#E5E7EB] bg-[#F3F4F6]"
                                            ></div>
                                        </div>
                                        <div
                                            class="h-6 w-24 animate-pulse rounded-full rounded-[12.8px] border border-[#E5E7EB] bg-[#F3F4F6]"
                                        ></div>
                                    </div>

                                    <div
                                        class="mt-5 rounded-[12.8px] border border-[#E5E7EB] bg-[#F3F4F6] p-6"
                                    >
                                        <div
                                            class="mx-auto h-10 w-24 animate-pulse rounded rounded-[12.8px] border border-[#E5E7EB] bg-[#F3F4F6]"
                                        ></div>
                                        <div
                                            class="mx-auto mt-2 h-4 w-32 animate-pulse rounded rounded-[12.8px] border border-[#E5E7EB] bg-[#F3F4F6]"
                                        ></div>
                                        <div
                                            class="mx-auto mt-2 h-3 w-40 animate-pulse rounded rounded-[12.8px] border border-[#E5E7EB] bg-[#F3F4F6]"
                                        ></div>
                                    </div>

                                    <ul class="mt-6 space-y-3">
                                        <li
                                            v-for="featureIdx in 5"
                                            :key="`free-plan-loading-feature-${featureIdx}`"
                                            class="flex items-center gap-3"
                                        >
                                            <div
                                                class="h-4 w-4 animate-pulse rounded-full rounded-[12.8px] border border-[#E5E7EB] bg-[#F3F4F6]"
                                            ></div>
                                            <div
                                                class="h-4 w-56 max-w-full animate-pulse rounded rounded-[12.8px] border border-[#E5E7EB] bg-[#F3F4F6]"
                                            ></div>
                                        </li>
                                    </ul>
                                </div>

                                <div class="border-t border-[#E5E7EB] p-6">
                                    <div
                                        class="h-12 w-full animate-pulse rounded-xl rounded-[12.8px] border border-[#E5E7EB] bg-[#F3F4F6]"
                                    ></div>
                                </div>
                            </div>

                            <div
                                class="flex h-full flex-col rounded-2xl border border-[#E5E7EB] bg-white shadow-sm"
                            >
                                <div class="flex-1 p-6">
                                    <div
                                        class="flex items-start justify-between gap-4"
                                    >
                                        <div class="w-full">
                                            <div
                                                class="h-6 w-24 animate-pulse rounded bg-slate-200"
                                            ></div>
                                            <div
                                                class="mt-2 h-4 w-3/4 animate-pulse rounded bg-slate-200"
                                            ></div>
                                        </div>
                                        <div
                                            class="h-6 w-24 animate-pulse rounded-full bg-slate-200"
                                        ></div>
                                    </div>

                                    <div
                                        class="mt-5 rounded-[12.8px] border border-[#E5E7EB] bg-[#F3F4F6] p-6"
                                    >
                                        <div
                                            class="mx-auto h-10 w-28 animate-pulse rounded bg-slate-200"
                                        ></div>
                                        <div
                                            class="mx-auto mt-2 h-4 w-32 animate-pulse rounded bg-slate-200"
                                        ></div>
                                        <div
                                            class="mx-auto mt-2 h-3 w-24 animate-pulse rounded bg-slate-200"
                                        ></div>
                                    </div>

                                    <div
                                        class="mt-4 flex items-center justify-center"
                                    >
                                        <div
                                            class="h-[42px] w-[212px] animate-pulse rounded-[12.75px] border border-slate-200 bg-slate-200"
                                        ></div>
                                    </div>

                                    <ul class="mt-6 space-y-3">
                                        <li
                                            v-for="featureIdx in 5"
                                            :key="`scale-plan-loading-feature-${featureIdx}`"
                                            class="flex items-center gap-3"
                                        >
                                            <div
                                                class="h-4 w-4 animate-pulse rounded-full bg-slate-200"
                                            ></div>
                                            <div
                                                class="h-4 w-56 max-w-full animate-pulse rounded bg-slate-200"
                                            ></div>
                                        </li>
                                    </ul>
                                </div>

                                <div class="border-t border-[#E5E7EB] p-6">
                                    <div
                                        class="h-12 w-full animate-pulse rounded-xl bg-slate-200"
                                    ></div>
                                </div>
                            </div>
                        </div>

                        <div v-else class="grid gap-6 lg:grid-cols-1">
                            <div class="grid gap-6 lg:grid-cols-2">
                                <!-- Free -->
                                <div
                                    class="flex h-full flex-col rounded-2xl border border-[#E5E7EB] bg-white shadow-sm"
                                >
                                    <div class="flex-1 p-6">
                                        <div
                                            class="flex items-start justify-between gap-4"
                                        >
                                            <div class="w-full">
                                                <div
                                                    class="flex items-start justify-between gap-3"
                                                >
                                                    <h3
                                                        class="text-lg font-semibold text-slate-900"
                                                    >
                                                        Free
                                                    </h3>
                                                    <span
                                                        v-if="
                                                            currentPlanKey ===
                                                            'free'
                                                        "
                                                        class="plan-badge-current inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold"
                                                    >
                                                        {{
                                                            $t(
                                                                "vue.current_plan",
                                                            )
                                                        }}
                                                    </span>
                                                </div>
                                                <p
                                                    class="mt-1 text-sm text-slate-500"
                                                >
                                                    {{
                                                        $t(
                                                            "vue.free_plan_subtitle",
                                                        )
                                                    }}
                                                </p>
                                            </div>
                                        </div>

                                        <div
                                            class="mt-5 flex h-48 flex-col items-center justify-center rounded-[12.8px] border border-[#E5E7EB] bg-[#F3F4F6] p-6 text-center"
                                        >
                                            <div
                                                class="text-4xl font-semibold text-slate-900"
                                            >
                                                $0
                                            </div>
                                            <div
                                                class="mt-1 text-sm text-slate-600"
                                            >
                                                {{
                                                    $t(
                                                        "vue.billing_price_forever",
                                                    )
                                                }}
                                            </div>
                                            <div
                                                class="mt-1 text-xs text-slate-500"
                                            >
                                                {{
                                                    $t(
                                                        "vue.billing_no_credit_card_required",
                                                    )
                                                }}
                                            </div>
                                        </div>

                                        <ul class="mt-6 space-y-3">
                                            <li class="flex items-start gap-3">
                                                <span class="plan-feature-check"
                                                    >✓</span
                                                ><span
                                                    class="text-sm text-slate-600"
                                                    >{{
                                                        $t(
                                                            "vue.plan_feature_one_domain",
                                                        )
                                                    }}</span
                                                >
                                            </li>
                                            <li class="flex items-start gap-3">
                                                <span class="plan-feature-check"
                                                    >✓</span
                                                ><span
                                                    class="text-sm text-slate-600"
                                                    >{{
                                                        $t(
                                                            "vue.free_plan_feature_emails_limit",
                                                        )
                                                    }}</span
                                                >
                                            </li>
                                            <li class="flex items-start gap-3">
                                                <span class="plan-feature-check"
                                                    >✓</span
                                                ><span
                                                    class="text-sm text-slate-600"
                                                    >{{
                                                        $t(
                                                            "vue.plan_feature_30_days_history",
                                                        )
                                                    }}</span
                                                >
                                            </li>
                                            <li class="flex items-start gap-3">
                                                <span class="plan-feature-check"
                                                    >✓</span
                                                ><span
                                                    class="text-sm text-slate-600"
                                                    >{{
                                                        $t(
                                                            "vue.plan_feature_ai_monitoring",
                                                        )
                                                    }}</span
                                                >
                                            </li>
                                            <li class="flex items-start gap-3">
                                                <span class="plan-feature-check"
                                                    >✓</span
                                                ><span
                                                    class="text-sm text-slate-600"
                                                    >{{
                                                        $t(
                                                            "vue.plan_feature_basic_reports",
                                                        )
                                                    }}</span
                                                >
                                            </li>
                                        </ul>
                                    </div>

                                    <div
                                        v-if="isBillingManagementRole"
                                        class="border-t border-[#E5E7EB] p-6"
                                    >
                                        <button
                                            type="button"
                                            class="w-full rounded-[6.75px] px-4 py-3 text-sm font-semibold"
                                            :class="
                                                currentPlanKey === 'free-plan'
                                                    ? 'cursor-not-allowed bg-slate-100 text-slate-400'
                                                    : 'bg-[#088bea] text-white hover:bg-blue-700 cursor-pointer'
                                            "
                                            @click="handleFreeCta"
                                        >
                                            {{ freeCtaLabel }}
                                        </button>
                                    </div>
                                </div>

                                <!-- Scale -->
                                <div
                                    class="flex h-full flex-col rounded-2xl border border-[#E5E7EB] bg-white shadow-sm"
                                >
                                    <div class="flex-1 p-6">
                                        <div
                                            class="flex items-start justify-between gap-4"
                                        >
                                            <div class="w-full">
                                                <div
                                                    class="flex items-start justify-between gap-3"
                                                >
                                                    <h3
                                                        class="text-lg font-semibold text-slate-900"
                                                    >
                                                        Scale
                                                    </h3>
                                                    <span
                                                        v-if="
                                                            currentPlanKey ===
                                                            'scale'
                                                        "
                                                        class="plan-badge-current inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold"
                                                    >
                                                        {{
                                                            $t(
                                                                "vue.current_plan",
                                                            )
                                                        }}
                                                    </span>
                                                    <span
                                                        v-else
                                                        class="inline-flex items-center rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700"
                                                    >
                                                        {{
                                                            $t(
                                                                "vue.scale_plan_badge_most_popular",
                                                            )
                                                        }}
                                                    </span>
                                                </div>
                                                <p
                                                    class="mt-1 text-sm text-slate-500"
                                                >
                                                    {{
                                                        $t(
                                                            "vue.scale_plan_subtitle",
                                                        )
                                                    }}
                                                </p>
                                            </div>
                                        </div>

                                        <div
                                            class="mt-5 flex h-48 flex-col items-center justify-center rounded-[12.8px] border border-[#E5E7EB] bg-[#F3F4F6] p-6 text-center"
                                        >
                                            <div
                                                class="text-4xl font-semibold text-slate-900"
                                            >
                                                {{ scalePriceMain }}
                                            </div>
                                            <div
                                                class="mt-1 text-sm text-slate-600"
                                            >
                                                {{
                                                    $t(
                                                        "vue.scale_plan_price_per_domain_month",
                                                    )
                                                }}
                                            </div>
                                            <div
                                                class="mt-1 text-xs text-slate-500 h-[13px]"
                                            >
                                                <span v-if="payYearly">
                                                    {{
                                                        $t(
                                                            "vue.scale_plan_price_yearly_note",
                                                        )
                                                    }}
                                                </span>
                                            </div>

                                            <div
                                                class="mt-4 flex items-center justify-center"
                                            >
                                                <div
                                                    class="rounded-[12.75px] border border-slate-200 bg-slate-100 px-3 py-2"
                                                >
                                                    <Switch
                                                        v-model="payYearly"
                                                        :left-label="
                                                            $t(
                                                                'vue.pay_monthly',
                                                            )
                                                        "
                                                        :right-label="
                                                            $t('vue.pay_yearly')
                                                        "
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        <ul class="mt-6 space-y-3">
                                            <li class="flex items-start gap-3">
                                                <span class="plan-feature-check"
                                                    >✓</span
                                                ><span
                                                    class="text-sm text-slate-600"
                                                    >{{
                                                        $t(
                                                            "vue.scale_plan_feature_2_50_domains",
                                                        )
                                                    }}</span
                                                >
                                            </li>
                                            <li class="flex items-start gap-3">
                                                <span class="plan-feature-check"
                                                    >✓</span
                                                ><span
                                                    class="text-sm text-slate-600"
                                                    >{{
                                                        $t(
                                                            "vue.scale_plan_feature_1_year_history",
                                                        )
                                                    }}</span
                                                >
                                            </li>
                                            <li class="flex items-start gap-3">
                                                <span class="plan-feature-check"
                                                    >✓</span
                                                ><span
                                                    class="text-sm text-slate-600"
                                                    >{{
                                                        $t(
                                                            "vue.scale_plan_feature_email_reports_smart_alerts",
                                                        )
                                                    }}</span
                                                >
                                            </li>
                                            <li class="flex items-start gap-3">
                                                <span class="plan-feature-check"
                                                    >✓</span
                                                ><span
                                                    class="text-sm text-slate-600"
                                                    >{{
                                                        $t(
                                                            "vue.scale_plan_feature_team_collaboration",
                                                        )
                                                    }}</span
                                                >
                                            </li>
                                            <li class="flex items-start gap-3">
                                                <span class="plan-feature-check"
                                                    >✓</span
                                                ><span
                                                    class="text-sm text-slate-600"
                                                    >{{
                                                        $t(
                                                            "vue.scale_plan_feature_api_access",
                                                        )
                                                    }}</span
                                                >
                                            </li>
                                        </ul>
                                    </div>

                                    <div
                                        v-if="isBillingManagementRole"
                                        class="border-t border-[#E5E7EB] p-6"
                                    >
                                        <button
                                            type="button"
                                            class="w-full rounded-[6.75px] px-4 py-3 text-sm font-semibold"
                                            :class="
                                                currentPlanKey === 'scale'
                                                    ? 'cursor-not-allowed bg-slate-100 text-slate-400'
                                                    : 'bg-[#088bea] text-white hover:bg-blue-700 cursor-pointer'
                                            "
                                            @click="handleScaleCta"
                                        >
                                            {{ scaleCtaLabel }}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <!-- 3) Custom / Big teams (static) -->
                <section
                    v-if="!hasGodMode"
                    class="mt-5 grid-cols-layout pb-5 lg:grid"
                >
                    <div class="mb-6 lg:mb-0 lg:mt-4">
                        <h3
                            class="mb-[5px] text-[15.75px] font-medium leading-[142%] tracking-[-0.01em] text-[#161E2E]"
                        >
                            {{ $t("vue.dmarceye_for_big_teams") }}
                        </h3>
                        <p
                            class="max-w-[333px] text-[12.25px] font-normal leading-[142%] text-[#4B5563]"
                        >
                            {{
                                $t("vue.reach_out_to_our_sales_tailored_offer")
                            }}
                        </p>
                    </div>

                    <div
                        v-if="isLoading"
                        class="mx-auto w-full rounded-2xl border border-[#E5E7EB] bg-white shadow-sm lg:mx-0"
                    >
                        <div class="p-6">
                            <div
                                class="flex flex-wrap items-start justify-between gap-6"
                            >
                                <div class="w-full lg:max-w-[680px]">
                                    <div
                                        class="h-7 w-52 animate-pulse rounded rounded-[12.8px] border border-[#E5E7EB] bg-[#F3F4F6]"
                                    ></div>
                                    <div
                                        class="mt-2 h-4 w-72 max-w-full animate-pulse rounded rounded-[12.8px] border border-[#E5E7EB] bg-[#F3F4F6]"
                                    ></div>

                                    <div class="mt-6 grid gap-y-3">
                                        <div
                                            v-for="idx in 8"
                                            :key="`custom-plan-loading-row-${idx}`"
                                            class="grid grid-cols-[140px_1fr] gap-4"
                                        >
                                            <div
                                                class="h-4 w-full animate-pulse rounded rounded-[12.8px] border border-[#E5E7EB] bg-[#F3F4F6]"
                                            ></div>
                                            <div
                                                class="h-4 w-48 max-w-full animate-pulse rounded rounded-[12.8px] border border-[#E5E7EB] bg-[#F3F4F6]"
                                            ></div>
                                        </div>
                                    </div>
                                </div>

                                <div class="w-full lg:w-auto">
                                    <div
                                        class="h-10 w-full animate-pulse rounded-xl rounded-[12.8px] border border-[#E5E7EB] bg-[#F3F4F6] lg:w-40"
                                    ></div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div
                        v-else
                        class="mx-auto w-full rounded-2xl border border-[#E5E7EB] bg-white shadow-sm lg:mx-0"
                    >
                        <div class="p-6">
                            <div>
                                <h2
                                    class="text-xl font-semibold text-slate-900"
                                >
                                    {{ $t("vue.custom_plan_title") }}
                                </h2>
                                <p class="mt-1 text-sm text-slate-500">
                                    {{ $t("vue.custom_plan_subtitle") }}
                                </p>

                                <ul class="mt-6 space-y-3">
                                    <li class="flex items-start gap-3">
                                        <span class="plan-feature-check">✓</span
                                        ><span class="text-sm text-slate-600">{{
                                            $t(
                                                "vue.custom_plan_feature_custom_domain_limit",
                                            )
                                        }}</span>
                                    </li>
                                    <li class="flex items-start gap-3">
                                        <span class="plan-feature-check">✓</span
                                        ><span class="text-sm text-slate-600">{{
                                            $t(
                                                "vue.custom_plan_feature_custom_email_limit",
                                            )
                                        }}</span>
                                    </li>
                                    <li class="flex items-start gap-3">
                                        <span class="plan-feature-check">✓</span
                                        ><span class="text-sm text-slate-600">{{
                                            $t(
                                                "vue.custom_plan_feature_unlimited_data_history",
                                            )
                                        }}</span>
                                    </li>
                                    <li class="flex items-start gap-3">
                                        <span class="plan-feature-check">✓</span
                                        ><span class="text-sm text-slate-600">{{
                                            $t(
                                                "vue.custom_plan_feature_ai_powered_monitoring",
                                            )
                                        }}</span>
                                    </li>
                                    <li class="flex items-start gap-3">
                                        <span class="plan-feature-check">✓</span
                                        ><span class="text-sm text-slate-600">{{
                                            $t(
                                                "vue.custom_plan_feature_email_reports_smart_alerts",
                                            )
                                        }}</span>
                                    </li>
                                    <li class="flex items-start gap-3">
                                        <span class="plan-feature-check">✓</span
                                        ><span class="text-sm text-slate-600">{{
                                            $t(
                                                "vue.custom_plan_feature_team_collaboration",
                                            )
                                        }}</span>
                                    </li>
                                    <li class="flex items-start gap-3">
                                        <span class="plan-feature-check">✓</span
                                        ><span class="text-sm text-slate-600">{{
                                            $t(
                                                "vue.custom_plan_feature_multi_tenant_architecture",
                                            )
                                        }}</span>
                                    </li>
                                    <li class="flex items-start gap-3">
                                        <span class="plan-feature-check">✓</span
                                        ><span class="text-sm text-slate-600">{{
                                            $t(
                                                "vue.custom_plan_feature_api_access",
                                            )
                                        }}</span>
                                    </li>
                                </ul>
                            </div>
                        </div>

                        <div
                            v-if="isBillingManagementRole"
                            class="flex justify-end border-t border-[#EDEFF1] px-[21px] py-[21px]"
                        >
                            <AtomicButton
                                class="mt-0"
                                color="light-blue"
                                @click="openSalesContact"
                            >
                                {{ $t("vue.contact_sales") }}
                            </AtomicButton>
                        </div>
                    </div>
                </section>
            </div>

            <FreePlanSwitchPopup
                v-model="freeModalOpen"
                :mode="freeModalMode"
                @success="handleFreeSwitchSuccess"
            />

            <!-- Existing modals -->
            <SubscriptionBuyModal
                v-if="selectedPlan"
                :paddle-client-token="props.paddleClientToken"
                :selected-plan="selectedPlan"
                :paddle-environment="props.paddleEnvironment"
                :domains-count="domainsCountToBuy"
                :period="payYearly ? 'yearly' : 'monthly'"
                @close="handleCloseSubscriptionModal"
            />

            <SubscriptionSwitchFrequencyModal
                v-if="showFrequencySwitchModal"
                :current-frequency="currentFrequency"
                :current-price="currentPaddleSubscription?.totals?.total || '0'"
                :current-plan-name="
                    currentPaddleSubscription?.items?.[0]?.productName
                        ?.replace(/DMARC\/|eye\s*-\s*/g, '')
                        .trim() || t('vue.unknown_plan')
                "
                :next-billing-date="
                    currentPaddleSubscription?.next_billing_at?.date || ''
                "
                @close="handleFrequencyModalClose"
                @success="handleFrequencySuccess"
            />

            <SubscriptionSwitchPlanModal
                v-if="showPlanSwitchModal && selectedPlanForSwitch"
                :current-frequency="currentFrequency"
                :current-price="currentPaddleSubscription?.totals?.total || '0'"
                :current-plan-name="
                    currentPaddleSubscription?.items?.[0]?.productName
                        ?.replace(/DMARC\/|eye\s*-\s*/g, '')
                        .trim() || t('vue.unknown_plan')
                "
                :next-billing-date="
                    currentPaddleSubscription?.next_billing_at?.date || ''
                "
                :new-plan-price-id="selectedPlanForSwitch.priceId"
                :new-plan-name="selectedPlanForSwitch.planName"
                :new-frequency="selectedPlanForSwitch.frequency"
                @close="handlePlanSwitchModalClose"
                @success="handlePlanSwitchSuccess"
            />

            <!--            <SubscriptionSwitchFrequencyModal-->
            <!--                v-if="showFrequencySwitchModal"-->
            <!--                :current-frequency="currentFrequency"-->
            <!--                :current-price="currentPaddleSubscription?.totals?.total || '0'"-->
            <!--                :current-plan-name="currentSubscribedPlanName"-->
            <!--                :next-billing-date="-->
            <!--                    currentPaddleSubscription?.next_billing_at?.date || ''-->
            <!--                "-->
            <!--                @close="handleFrequencyModalClose"-->
            <!--                @success="handleFrequencySuccess"-->
            <!--            />-->

            <!--            <SubscriptionSwitchPlanModal-->
            <!--                v-if="showPlanSwitchModal && selectedPlanForSwitch"-->
            <!--                :current-frequency="currentFrequency"-->
            <!--                :current-price="currentPaddleSubscription?.totals?.total || '0'"-->
            <!--                :current-plan-name="currentSubscribedPlanName"-->
            <!--                :next-billing-date="-->
            <!--                    currentPaddleSubscription?.next_billing_at?.date || ''-->
            <!--                "-->
            <!--                :new-plan-price-id="selectedPlanForSwitch.priceId"-->
            <!--                :new-plan-name="selectedPlanForSwitch.planName"-->
            <!--                :new-frequency="selectedPlanForSwitch.frequency"-->
            <!--                @close="handlePlanSwitchModalClose"-->
            <!--                @success="handlePlanSwitchSuccess"-->
            <!--            />-->
        </BillingTabs>

        <TrialEndedPopup />
        <!--        <ExtendYourTrialPopup />-->
        <!--        <FreePlanPopup />-->
        <FreePlanMaxReachedPopup />
        <FreePlanPostContinuePopup v-model="freePlanPostContinuePopupOpen" />
        <FreePlanPostDowngradePopup v-model="freePlanPostDowngradePopupOpen" />
    </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref, type PropType } from "vue";
import { router, usePage } from "@inertiajs/vue3";
import { type IPaddleSubscription } from "@/types/Subscriptions/IPaddleSubscription";
import route from "ziggy-js";
import axios from "axios";
import type { IRhizomPageProps } from "@/types/RhizomPageProps/RhizomPageProps";
import { useTranslations } from "@/composables/useTranslations.ts";
import FreePlanSwitchPopup from "@/components/Atomic/Dialogs/Popups/FreePlanSwitchPopup.vue";
import Switch from "@/components/Atomic/Switch/Switch.vue";
import FreePlanMaxReachedPopup from "@/components/Atomic/Dialogs/Popups/FreePlanMaxReachedPopup.vue";
import FreePlanPostContinuePopup from "@/components/Atomic/Dialogs/Popups/FreePlanPostContinuePopup.vue";
import FreePlanPostDowngradePopup from "@/components/Atomic/Dialogs/Popups/FreePlanPostDowngradePopup.vue";
import WarningBlock from "@/components/Atomic/Miscellaneous/WarningBlock.vue";
import { useFreePlanMaxReachedPopup } from "@/composables/useFreePlanMaxReachedPopup";

const { t, locale } = useTranslations();
const { showFreePlanReportsLimitBlock, freePlanResetDateLabel } =
    useFreePlanMaxReachedPopup();

const props = defineProps({
    isTrialing: { type: Boolean, default: false },
    isFreePlan: { type: Boolean, default: false },
    isSubscribed: { type: Boolean, default: false },
    trialEndsAt: { type: String, default: "" },
    domainsCount: { type: Number, default: () => 0 },

    domains: { type: Array as PropType<string[]>, default: () => [] },

    freeModalIllustrationSrc: { type: String, default: "" },

    paddleClientToken: { type: String, default: "" },
    paddleEnvironment: {
        type: String as () => "production" | "sandbox",
        default: "production",
    },
    paddleSubscription: {
        type: Object as PropType<IPaddleSubscription | null>,
        default: null,
    },
    paddleCustomerPortalUrl: { type: String, default: "" },

    plans: {
        type: Array as () => {
            type: "monthly" | "yearly";
            priceId: string;
            productId: string;
            productName: string;
            productDescription: string;
            totalPrice?: number;
        }[],
        default: () => [],
    },

    isOwner: { type: Boolean, default: false },
    teamRole: { type: String, default: "member" },
});

const billingAdminTeamRoles = ["editor", "viewer"];

const isLoading = ref(true);
const asyncPaddleSubscription = ref<IPaddleSubscription | null>(null);
const asyncPaddleCustomerPortalUrl = ref<string>("");
const asyncPlans = ref<typeof props.plans>([]);

const currentPaddleSubscription = computed(
    () => asyncPaddleSubscription.value || props.paddleSubscription,
);
const currentPaddleCustomerPortalUrl = computed(
    () => asyncPaddleCustomerPortalUrl.value || props.paddleCustomerPortalUrl,
);
const currentPlans = computed(() =>
    asyncPlans.value.length > 0 ? asyncPlans.value : props.plans,
);

const payYearly = ref(true);

const inertiaProps = usePage<IRhizomPageProps>();
const hasGodMode = computed(() => inertiaProps.props.hasGodMode);

const loadSubscriptionData = async () => {
    try {
        const response = await axios.get(route("billing.subscriptions.data"));
        asyncPaddleSubscription.value = response.data.paddleSubscription;
        asyncPaddleCustomerPortalUrl.value =
            response.data.paddleCustomerPortalUrl;
    } catch (error) {
        console.error("Failed to load subscription data:", error);
    }
};

const loadPlans = async () => {
    try {
        const response = await axios.get(route("billing.subscriptions.plans"));
        asyncPlans.value = response.data.plans;
    } catch (error) {
        console.error("Failed to load plans:", error);
    }
};

const loadAllData = async () => {
    isLoading.value = true;
    await Promise.all([loadSubscriptionData(), loadPlans()]);
    isLoading.value = false;
};

const normalizePlanName = (s?: string) =>
    (s || "")
        .replace(/DMARC\/|eye\s*-\s*/g, "")
        .trim()
        .toLowerCase();

const isBillingManagementRole = computed<boolean>(
    () => props.isOwner || ["owner", "admin"].includes(props.teamRole),
);

const currentPlanKey = computed<
    "trial" | "trial-expired" | "free-plan" | "scale" | "enterprise"
>(() => {
    if (hasGodMode.value) return "enterprise";
    if (props.isFreePlan) return "free-plan";
    if (props.isTrialing && !props.isSubscribed) return "trial";
    if (!props.isTrialing && !props.isSubscribed) return "trial-expired";

    const name = normalizePlanName(
        currentPaddleSubscription.value?.items?.[0]?.productName,
    );
    if (name.includes("scale")) return "scale";

    return "trial";
});

const plansSectionTitle = computed(() =>
    currentPlanKey.value === "trial" || currentPlanKey.value === "trial-expired"
        ? t("vue.available_dmarc_subscription")
        : t("vue.all_plans"),
);

// const currentSubscribedPlanName = computed(() => {
//     return (
//         currentPaddleSubscription.value?.items?.[0]?.productName
//             ?.replace(/DMARC\/|eye\s*-\s*/g, "")
//             .trim() || t("vue.unknown_plan")
//     );
// });

const currentPlanTitle = computed(() => {
    if (currentPlanKey.value === "enterprise")
        return `${t("vue.enterprise")} ${t("vue.plan")}`;
    if (currentPlanKey.value === "trial") return t("vue.free_trial");
    if (currentPlanKey.value === "trial-expired")
        return t("vue.free_trial_expired");
    if (currentPlanKey.value === "free-plan") return t("vue.free_plan_title");
    if (currentPlanKey.value === "scale") return t("vue.scale_plan_title");
    if (currentPlanKey.value === "trial-expired") return t("vue.trial_expired");
    return t("vue.unknown_plan");
});

const currentPlanSubtitle = computed(() => {
    if (currentPlanKey.value === "enterprise")
        return t("vue.billing_enterprise_unlimited_access");
    if (currentPlanKey.value === "trial")
        return t("vue.explore_all_features_free");
    if (currentPlanKey.value === "trial-expired")
        return t("vue.billing_trial_expired_subtitle");
    if (currentPlanKey.value === "free-plan")
        return t("vue.free_plan_subtitle");
    return (
        currentPaddleSubscription.value?.items?.[0]?.productDescription ||
        t("vue.no_description_available")
    );
});

const currentPlanNotice = computed(() => {
    if (currentPlanKey.value === "trial-expired") {
        return t("vue.trial_expired_notice");
    }
    return "";
});

const currentFrequency = computed(() => {
    return (currentPaddleSubscription.value?.period as string) || "month";
});

const FREE_PLAN_EMAIL_QUOTA_LIMIT = 5000;

const freePlanEmailQuotaUsed = computed(() => {
    const value = Number(
        inertiaProps.props.freePlanLastPeriodNumberOfReport ?? 0,
    );

    if (Number.isNaN(value) === true) {
        return 0;
    }

    return Math.max(0, Math.min(FREE_PLAN_EMAIL_QUOTA_LIMIT, value));
});

const freePlanEmailQuotaResetDateLabel = computed(() => {
    const restartedAt = inertiaProps.props.team?.free_plan_access_restarted_at;

    if (!restartedAt) {
        return "—";
    }

    const parsedDate = restartedAt.includes("T")
        ? new Date(restartedAt)
        : new Date(`${restartedAt}T00:00:00`);

    if (Number.isNaN(parsedDate.getTime()) === true) {
        return restartedAt;
    }

    const nextRenewalDate = new Date(parsedDate);
    nextRenewalDate.setDate(nextRenewalDate.getDate() + 30);

    return new Intl.DateTimeFormat(locale.value === "cs" ? "cs-CZ" : "en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
    }).format(nextRenewalDate);
});

const freePlanEmailQuotaValue = computed(() => {
    const numberFormat = new Intl.NumberFormat(
        locale.value === "cs" ? "cs-CZ" : "en-US",
    );

    return t("vue.billing_email_quota_value", {
        used: numberFormat.format(freePlanEmailQuotaUsed.value),
        limit: numberFormat.format(FREE_PLAN_EMAIL_QUOTA_LIMIT),
        date: freePlanEmailQuotaResetDateLabel.value,
    });
});

const currentPlanRows = computed(() => {
    if (currentPlanKey.value === "enterprise") {
        return [
            { label: t("vue.data_history"), value: t("vue.unlimited") },
            { label: t("vue.features"), value: t("vue.all_features_enabled") },
        ];
    }

    if (currentPlanKey.value === "trial") {
        return [
            { label: t("vue.trial_status"), value: t("vue.active") },
            { label: t("vue.trial_expires"), value: props.trialEndsAt || "—" },
            {
                label: t("vue.your_domains"),
                value: `${props.domainsCount} ${props.domainsCount === 1 ? t("vue.domain") : t("vue.domains")}`,
            },
        ];
    }

    if (currentPlanKey.value === "trial-expired") {
        return [
            { label: t("vue.trial_status"), value: t("vue.expired") },
            { label: "Trial Ended", value: props.trialEndsAt || "—" },
            {
                label: t("vue.your_domains"),
                value: `${props.domainsCount} ${props.domainsCount === 1 ? t("vue.domain") : t("vue.domains")}`,
            },
        ];
    }

    if (props.isSubscribed && currentPlanKey.value === "scale") {
        const canceled = !currentPaddleSubscription.value?.next_billing_at;
        const isYear = currentPaddleSubscription.value?.period === "year";
        const nextDate = currentPaddleSubscription.value?.next_billing_at?.date;

        return [
            {
                label: t("vue.payment_cycle"),
                value: canceled
                    ? t("vue.scheduled_to_be_canceled")
                    : isYear
                      ? t("vue.yearly")
                      : t("vue.monthly"),
                onClick: canceled ? undefined : () => updateFrequency(),
            },
            {
                label:
                    currentPaddleSubscription.value?.change?.action === "cancel"
                        ? t("vue.scheduled_to_be_canceled_at")
                        : t("vue.next_invoice"),
                value: nextDate
                    ? new Date(nextDate).toLocaleDateString("en-US", {
                          day: "numeric",
                          month: "long",
                          year: "numeric",
                      })
                    : "N/A",
            },
            ...(currentPaddleSubscription.value?.change?.action !== "cancel"
                ? [
                      {
                          label: t("vue.next_payment"),
                          value: `$${currentPaddleSubscription.value?.totals?.total || "0"}`,
                      },
                  ]
                : []),
            {
                label: t("vue.your_domains"),
                value: `${props.domainsCount} ${props.domainsCount === 1 ? t("vue.domain") : t("vue.domains")}`,
            },
        ];
    }

    if (currentPlanKey.value === "free-plan") {
        return [
            { label: t("vue.plan_status"), value: t("vue.active") },
            {
                label: t("vue.price"),
                value: "$0" + " " + t("vue.billing_price_forever"),
            },
            {
                label: t("vue.your_domains"),
                value: `${props.domainsCount} ${props.domainsCount === 1 ? t("vue.domain") : t("vue.domains")}`,
            },
            {
                label: t("vue.billing_email_quota"),
                value: freePlanEmailQuotaValue.value,
            },
        ];
    }

    return [
        {
            label: t("vue.your_domains"),
            value: `${props.domainsCount} ${props.domainsCount === 1 ? t("vue.domain") : t("vue.domains")}`,
        },
    ];
});

const showManagePortalBtn = computed(() => !!currentPaddleSubscription.value);
// const showUpgradePlanBtn = computed(() => currentPlanKey.value === "free-plan");

const selectedPlan = ref(
    undefined as
        | {
              type: "monthly" | "yearly";
              priceId: string;
              productId: string;
              totalPrice?: number;
              productName: string;
              productDescription: string;
          }[]
        | undefined,
);

const showFrequencySwitchModal = ref(false);
const showPlanSwitchModal = ref(false);
const selectedPlanForSwitch = ref<{
    priceId: string;
    planName: string;
    frequency: "month" | "year";
} | null>(null);

const handleCloseSubscriptionModal = () => {
    selectedPlan.value = undefined;
    window.location.reload();
};

const handlePlanSelection = (plan: (typeof currentPlans.value)[0][]) => {
    selectedPlan.value = plan;
};

const switchToPlan = (plan: (typeof currentPlans.value)[0][]) => {
    let planToSwitch;
    if (payYearly.value) {
        planToSwitch = plan[1];
    } else {
        planToSwitch = plan[0];
    }

    // Show the plan switch modal instead of directly switching
    selectedPlanForSwitch.value = {
        priceId: planToSwitch.priceId,
        planName: planToSwitch.productName
            .replace(/DMARC\/|eye\s*-\s*/g, "")
            .trim(),
        frequency: planToSwitch.type === "yearly" ? "year" : "month",
    };
    showPlanSwitchModal.value = true;
};

const domainsCountToBuy = computed(() => {
    if (
        selectedPlan.value !== undefined &&
        selectedPlan.value[0].productName
            .replace(/DMARC\/|eye\s*-\s*/g, "")
            .trim() === "Scale"
    ) {
        return props.domainsCount < 1 ? 1 : props.domainsCount;
    }

    return props.domainsCount === 0 ? 1 : props.domainsCount;
});

function visitCustomerPortal() {
    if (currentPaddleCustomerPortalUrl.value) {
        window.open(currentPaddleCustomerPortalUrl.value, "_blank", "noopener");
    } else {
        console.error("Paddle customer portal URL is not set.");
    }
}

function openSalesContact() {
    window.open("https://zapti.me/dmarceye/sales", "_blank", "noopener");
}

function updateFrequency() {
    showFrequencySwitchModal.value = true;
}

const handleFrequencyModalClose = () => {
    showFrequencySwitchModal.value = false;
    loadSubscriptionData();
};

const handlePlanSwitchModalClose = () => {
    showPlanSwitchModal.value = false;
    selectedPlanForSwitch.value = null;
    loadSubscriptionData();
};

const handleFrequencySuccess = () => loadSubscriptionData();
const handlePlanSwitchSuccess = () => loadSubscriptionData();

/* CTA labels */
const freeCtaLabel = computed(() => {
    if (
        currentPlanKey.value === "trial" ||
        currentPlanKey.value === "trial-expired"
    )
        return t("vue.cta_continue_with_free");
    if (
        currentPlanKey.value === "scale" ||
        currentPlanKey.value === "enterprise"
    )
        return t("vue.cta_downgrade_to_free");
    if (currentPlanKey.value === "free-plan") return t("vue.current_plan");
    return t("vue.cta_continue_with_free");
});

const scaleCtaLabel = computed(() => {
    if (
        currentPlanKey.value === "trial" ||
        currentPlanKey.value === "trial-expired"
    )
        return t("vue.cta_subscribe_scale");
    if (currentPlanKey.value === "free-plan") return t("vue.cta_upgrade_scale");
    if (currentPlanKey.value === "scale") return t("vue.current_plan");
    return t("vue.cta_subscribe_scale");
});

/* Plan lookup helpers */
const groupedPlansByProduct = computed(() => {
    // Filter out the plan that user is currently subscribed to
    const filteredPlans = currentPlans.value.filter((plan) => {
        if (!props.isSubscribed || !currentPaddleSubscription.value) {
            return true; // Show all plans if not subscribed
        }

        // Check if this plan matches the current subscription by product name
        const currentProductName =
            currentPaddleSubscription.value.items[0]?.productName;
        return plan.productName !== currentProductName;
    });

    return filteredPlans.reduce(
        (acc, plan) => {
            if (!acc[plan.productId]) {
                acc[plan.productId] = [];
            }
            acc[plan.productId].push(plan);
            return acc;
        },
        {} as Record<string, typeof currentPlans.value>,
    );
});

const getPlanGroupByName = (name: "scale") => {
    const group = Object.values(groupedPlansByProduct.value).find((plans) =>
        plans.some((plan) =>
            normalizePlanName(plan.productName).includes(name),
        ),
    );

    if (!group?.length) return null;

    const monthly = group.find((p) => p.type === "monthly") || group[0];
    const yearly = group.find((p) => p.type === "yearly") || group[0];

    return [monthly, yearly] as const;
};

const toNumber = (v: unknown) => {
    const n =
        typeof v === "string" ? Number(v) : typeof v === "number" ? v : NaN;
    return Number.isFinite(n) ? n : 0;
};

const formatUsd = (amount: unknown) => {
    const n = toNumber(amount);
    return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: n % 1 === 0 ? 0 : 2,
    }).format(n);
};

const scalePlanGroup = computed(() => getPlanGroupByName("scale"));

const scaleMonthlyTotal = computed(
    () => toNumber(scalePlanGroup.value?.[0]?.totalPrice) || 4,
);
const scaleYearlyTotal = computed(
    () => toNumber(scalePlanGroup.value?.[1]?.totalPrice) || 48,
);

const scalePriceMain = computed(() =>
    formatUsd(
        payYearly.value ? scaleYearlyTotal.value / 12 : scaleMonthlyTotal.value,
    ),
);

/* Free modal */
const FREE_PLAN_POST_POPUP_KEY = "ui:billing:free_plan_post_popup_mode";

const freeModalOpen = ref(false);
const freeModalMode = ref<"downgrade" | "continue">("downgrade");
const freePlanPostContinuePopupOpen = ref(false);
const freePlanPostDowngradePopupOpen = ref(false);

const handleFreeCta = () => {
    if (currentPlanKey.value === "free-plan") return;
    freeModalMode.value =
        currentPlanKey.value === "trial" ||
        currentPlanKey.value === "trial-expired"
            ? "continue"
            : "downgrade";
    freeModalOpen.value = true;
};

const handleFreeSwitchSuccess = (mode: "downgrade" | "continue") => {
    localStorage.setItem(FREE_PLAN_POST_POPUP_KEY, mode);
    router.visit(route("billing.subscriptions.index"));
};

const consumePostFreePlanPopup = () => {
    const mode = localStorage.getItem(FREE_PLAN_POST_POPUP_KEY);
    if (mode !== "continue" && mode !== "downgrade") {
        return;
    }

    if (!props.isFreePlan) {
        return;
    }

    localStorage.removeItem(FREE_PLAN_POST_POPUP_KEY);
    freePlanPostContinuePopupOpen.value = mode === "continue";
    freePlanPostDowngradePopupOpen.value = mode === "downgrade";
};

/* Basic / Scale CTA */
const handleScaleCta = () => {
    if (currentPlanKey.value === "scale") return;
    const plan = getPlanGroupByName("scale");
    if (!plan) return;

    if (props.isSubscribed) switchToPlan(plan);
    else handlePlanSelection(plan);
};

onMounted(async () => {
    await loadAllData();
    consumePostFreePlanPopup();
});
</script>

<style scoped>
.plan-feature-check {
    display: inline-flex;
    min-width: 16px;
    align-items: center;
    justify-content: center;
    color: #059669;
    font-size: 20px;
    line-height: 1;
    font-weight: 600;
    transform: translateY(-1px);
}

.plan-badge-current {
    background-color: #d1fae5;
    color: #059669;
}

.tooltip-triangle {
    width: 14px;
    height: 14px;
    background: linear-gradient(to bottom right, transparent 55%, #1f2937 45%);
    transform: rotate(45deg);
    position: absolute;
    border-radius: 2px;
    bottom: -6px;
    left: 0;
    right: 0;
    margin: auto;
    z-index: 2;
}
</style>
